import { useState, useEffect } from 'react';
import {
  parseAppManifest,
  toManifestError,
  DEV_HTTP_ALLOWLIST,
  type AppManifest,
  type ManifestError,
} from '@aid/shared';

export interface ManifestState {
  manifest: AppManifest | null;
  loading: boolean;
  error: ManifestError | null;
}

const MANIFEST_URL = '/app-manifest.json';

export function useManifest(): ManifestState {
  const [state, setState] = useState<ManifestState>({
    manifest: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState({ manifest: null, loading: true, error: null });

      try {
        // no-store: the catalog must always reflect the deployed manifest.
        // A stale HTTP-cached copy would hide newly added apps (or their
        // artwork) after a deploy, and dev-server 304s have shown the same
        // symptom with old art while iterating locally.
        const response = await fetch(MANIFEST_URL, { cache: 'no-store' });

        if (!response.ok) {
          throw new Error(`Failed to fetch manifest: ${response.status}`);
        }

        const data: unknown = await response.json();

        if (cancelled) return;

        const manifest = parseAppManifest(data, {
          allowHttpHosts: [...DEV_HTTP_ALLOWLIST],
        });

        if (!cancelled) {
          setState({ manifest, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            manifest: null,
            loading: false,
            error: toManifestError(err),
          });
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
